import type { Mesh, Object3D } from 'three';

export interface MeshStats {
  total: number;
  visible: number;
}

export function collectMeshes(root: Object3D): Mesh[] {
  const meshes: Mesh[] = [];
  root.traverse((obj) => {
    if ((obj as Mesh).isMesh) meshes.push(obj as Mesh);
  });
  return meshes;
}

export function meshStats(root: Object3D): MeshStats {
  const meshes = collectMeshes(root);
  return {
    total: meshes.length,
    visible: meshes.filter((m) => m.visible).length,
  };
}

export function isolateMesh(root: Object3D, target: Mesh): void {
  for (const mesh of collectMeshes(root)) {
    mesh.visible = mesh === target;
  }
}

export function showAllMeshes(root: Object3D): void {
  for (const mesh of collectMeshes(root)) {
    mesh.visible = true;
  }
}

export type MeshIndex = Map<number, Mesh[]>;

export function buildMeshIndex(meshes: Mesh[]): MeshIndex {
  const index: MeshIndex = new Map();
  for (const mesh of meshes) {
    const id = mesh.userData['expressID'];
    if (typeof id !== 'number') continue;
    const list = index.get(id) ?? [];
    list.push(mesh);
    index.set(id, list);
  }
  return index;
}

export function setVisibilityByIDs(index: MeshIndex, ids: number[], visible: boolean): void {
  for (const id of ids) {
    const meshes = index.get(id);
    if (!meshes) continue;
    for (const mesh of meshes) mesh.visible = visible;
  }
}

export interface DetachedMesh {
  mesh: Mesh;
  parent: Object3D;
}

/**
 * Removes an element's meshes from the scene graph without disposing them:
 * geometries and materials come from the loader's shared caches, and the
 * meshes themselves are kept alive so `reattach` can undo the removal.
 */
export function detachByIDs(index: MeshIndex, ids: number[]): DetachedMesh[] {
  const detached: DetachedMesh[] = [];
  for (const id of ids) {
    const meshes = index.get(id);
    if (!meshes) continue;
    for (const mesh of meshes) {
      const parent = mesh.parent;
      if (!parent) continue;
      parent.remove(mesh);
      detached.push({ mesh, parent });
    }
  }
  return detached;
}

export function reattach(detached: readonly DetachedMesh[]): void {
  for (const { mesh, parent } of detached) {
    parent.add(mesh);
  }
}

// Materials are intentionally not disposed: they come from the loader's
// shared material cache and outlive any single model.
export function disposeModelTree(root: Object3D): void {
  root.traverse((obj) => {
    const geometry = (obj as Mesh).geometry;
    if (geometry) geometry.dispose();
  });
  while (root.children.length > 0) {
    root.remove(root.children[0]!);
  }
}
